import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const AuthCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      // Get the current session after email verification
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }

      if (session?.user) {
        // User is verified and logged in
        setSuccess(true);
        
        // Check if user has a profile and redirect appropriately
        const { data: profile } = await supabase
          .from('member_profiles_2025_12_03_10_30')
          .select(`
            *,
            membership_types_2025_12_03_10_30 (
              name
            )
          `)
          .eq('user_id', session.user.id)
          .single();

        // Wait a moment before redirecting
        setTimeout(() => {
          if (profile?.is_admin) {
            navigate('/admin-dashboard');
          } else if (profile?.membership_type_id) {
            // Navigate to appropriate member portal
            const membershipName = profile.membership_types_2025_12_03_10_30?.name;
            const portalMap: { [key: string]: string } = {
              'Student Member': '/student-portal',
              'Academic Member': '/academic-portal',
              'Professional Member': '/professional-portal',
              'Chartered Member': '/chartered-portal',
              'Training Centre': '/training-centre-portal',
              'Corporate Bronze': '/bronze-portal',
              'Corporate Silver': '/silver-portal',
              'Corporate Gold': '/gold-portal'
            };
            navigate(portalMap[membershipName || ''] || '/member-dashboard');
          } else {
            navigate('/member-dashboard');
          }
        }, 2000);
      } else {
        // No session, redirect to login
        setTimeout(() => {
          navigate('/member-login');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Auth callback error:', error);
      setError(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualRedirect = () => {
    navigate('/member-dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
            <CardTitle>Verifying Your Account</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              Please wait while we verify your email and set up your account...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <CardTitle>Verification Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">{error}</p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/member-login')}>
                Go to Login
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <CardTitle>Email Verified Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your email has been verified and your account is now active. 
              You will be redirected to your dashboard shortly.
            </p>
            <Button onClick={handleManualRedirect}>
              Go to Dashboard Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default AuthCallback;